import React, { useState, useRef, useEffect } from 'react';

import d3 from './d3';
import epochs from '~/kits/general/config';
import Highlight from './Highlight';
import MouseIndicator from './MouseIndicator';
import ChartWrapper from '~/core/scripts/chart-components/ChartWrapper';
import XAxis from '~/core/scripts/chart-components/XAxis';
import YAxis from '~/core/scripts/chart-components/YAxis';
import Grid from '~/core/scripts/chart-components/Grid';
import { translate } from '../../../../core/scripts/chart-components/utils';
import { is768Query } from '~/core/scripts/mediaquery-helper';

const isSmallScreen = !is768Query();
const xTicks = [2013, 2014, 2015, 2016, 2017, 2018, 2019].map(value => ({ value: new Date(value, 0, 1), label: value }));
const yTicks = [0, 5, 10, 15, 20].map(value => ({ value, label: value }));
const yTicksGrid = [0, 2.5, 5, 7.5, 10, 12.5, 15, 17.5, 20].map(value => ({ value, label: value }));
const items = epochs.reduce((res, epoch) => res.concat(epoch.items), []);
const coordsKey = isSmallScreen ? 'coordsMobile' : 'coords';


export default ({
  width, height, margin, xScale,
  yScale, data, activeItem, activeEpochId
}) => {
  const [mouseX, setMouseX] = useState(null);
  const [bounds, setBounds] = useState(null);
  const chartNode = useRef(null);

  useEffect(() => {
    if (chartNode.current && !bounds) {
      setBounds(chartNode.current.getBoundingClientRect())
    }
  });

  const line = d3.line()
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const activeEpoch = epochs.find(e => e.id === activeEpochId);
  const epochStartX = xScale(activeEpoch.startDate);
  const epochEndX = xScale(activeEpoch.endDate);
  const epochRectWidth = epochEndX - epochStartX;

  const onMouseMove = evt => {
    if (!bounds) {
      return null;
    }

    setMouseX(evt.clientX - bounds.left);
  };

  const onMouseOut = () => setMouseX(null);

  return (
    <ChartWrapper width={width} height={height} ref={chartNode}>
      <XAxis
        margin={margin}
        width={width}
        height={height}
        ticks={xTicks}
        x={xScale}
      />
      <YAxis
        margin={margin}
        width={width}
        height={height}
        ticks={yTicks}
        y={yScale}
        unit="%"
      />
      <Grid
        margin={margin}
        width={width}
        height={height}
        y={yScale}
        x={xScale}
        values={{
          x: xTicks,
          y: yTicksGrid
        }}
      />
      <g transform={translate(margin.left, margin.top)}>
        <rect
          x={epochStartX}
          y={0}
          height={height - margin.top}
          width={epochRectWidth}
          className="chart__epoch"
        />
      </g>
      <path
        className="chart__line"
        d={line(data)}
      />
      <g transform={translate(margin.left, margin.top)}>
        <line
          className="chart__highlightline"
          x1={xScale(activeItem[coordsKey].x)}
          x2={xScale(activeItem[coordsKey].x)}
          y1={0}
          y2={height - margin.top}
        />
        <MouseIndicator
          x={mouseX}
          margin={margin}
          height={height}
          xScale={xScale}
        />
        <rect
          x={0}
          y={0}
          width={width - margin.right}
          height={height - margin.top}
          onMouseMove={onMouseMove}
          onMouseOut={onMouseOut}
          fillOpacity={0}
        />
      </g>
      {items.map(item => (
        <Highlight
          key={item.id}
          milestone={item.milestone}
          milestoneNumber={item.milestoneNumber}
          x={xScale(item[coordsKey].x)}
          y={yScale(item[coordsKey].y)}
          active={item.id === activeItem.id}
          onMouseMove={onMouseMove}
          id={item.id}
        />
      ))}
    </ChartWrapper>
  );
};
